/**
 * RecommendationOverview Component
 *
 * A recommendation register at a glance: a donut of FAA status beside an issue-year
 * timeline stacked by the same statuses. Both double as filters: a slice or legend entry toggles
 * that status, a year column toggles that year. Each chart counts the register with every filter
 * except its own, so choosing in one never empties it.
 *
 * Status is a state with meaning, so it wears the fixed status scale (good, warning, serious,
 * critical, and grey for the rest) and always comes with an icon and a label, never colour alone.
 */

import React from 'react';
import {Box, ButtonBase, Chip, Stack, SvgIconProps, Typography} from '@mui/material';
import {alpha} from '@mui/material/styles';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import {
    ActiveElement,
    ArcElement,
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    ChartData,
    ChartEvent,
    ChartOptions,
    LinearScale,
    Tooltip,
    TooltipItem,
} from 'chart.js';
import {Bar, Doughnut} from 'react-chartjs-2';
import {SafetyRecommendationStatusGroup} from '../../@types/safety-recommendation';
import {IssuedByYear, STATUS_GROUP_LABELS, STATUS_GROUP_ORDER} from './recommendations';

ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, Tooltip);

interface StatusStyle {
    color: string;
    Icon: React.ElementType<SvgIconProps>;
}

/** The fixed status scale: good, warning, serious, critical, and grey for closed for other reasons. */
export const STATUS_STYLE: Record<SafetyRecommendationStatusGroup, StatusStyle> = {
    CLOSED_ACCEPTABLE: {color: '#0ca30c', Icon: CheckCircleOutlineIcon},
    OPEN: {color: '#fab219', Icon: ScheduleIcon},
    OPEN_UNACCEPTABLE: {color: '#ec835a', Icon: ErrorOutlineIcon},
    CLOSED_UNACCEPTABLE: {color: '#d03b3b', Icon: HighlightOffIcon},
    CLOSED_OTHER: {color: '#8f8f8b', Icon: RemoveCircleOutlineIcon},
};

/** The surface colour between chart fills, so adjacent marks stay separate. */
const SURFACE = '#ffffff';

/** Chart fill: full strength, or faded when a filter is on and this mark is not part of it. */
const fill = (group: SafetyRecommendationStatusGroup, faded: boolean): string =>
    faded ? alpha(STATUS_STYLE[group].color, 0.2) : STATUS_STYLE[group].color;

const pointerOnMarks = (event: ChartEvent, elements: ActiveElement[]): void => {
    const target = event.native?.target as HTMLElement | undefined;
    if (target) target.style.cursor = elements.length > 0 ? 'pointer' : 'default';
};

interface RecommendationStatusChipProps {
    group: SafetyRecommendationStatusGroup;
    label: string;
    /** Makes the chip a button, e.g. to open the recommendation's correspondence. */
    onClick?: () => void;
    ariaLabel?: string;
}

/** A status chip: the icon carries the status colour, the text stays in text colour. */
export const RecommendationStatusChip: React.FC<RecommendationStatusChipProps> = ({group, label, onClick, ariaLabel}) => {
    const {color, Icon} = STATUS_STYLE[group];
    return (
        <Chip
            size="small"
            variant="outlined"
            icon={<Icon />}
            label={label}
            onClick={onClick}
            aria-label={ariaLabel}
            sx={{
                height: 'auto',
                maxWidth: '100%',
                borderColor: color,
                '& .MuiChip-icon': {color},
                '& .MuiChip-label': {whiteSpace: 'normal', py: 0.25},
            }}
        />
    );
};

export interface RecommendationOverviewProps {
    /** Recommendations the table shows, and how many there are before filtering. */
    shownCount: number;
    totalCount: number;
    /** How many investigations issued them. Shown under the count, where it reframes it. */
    investigationCount?: number;
    /** Status counts with every filter but the status filter. */
    statusCounts: Record<SafetyRecommendationStatusGroup, number>;
    /** Issue years with every filter but the issue-year range. */
    byYear: IssuedByYear;
    selectedStatuses: SafetyRecommendationStatusGroup[];
    /** The single issue year filtered to, if the year range is exactly one year. */
    /** The issue year range in force, either end open. The server sends only the years within it. */
    issuedFromYear: number | null;
    issuedToYear: number | null;
    onToggleStatus: (group: SafetyRecommendationStatusGroup) => void;
    onToggleYear: (year: number) => void;
}

/** What the timeline heading says about the year filter in force. */
const yearCaption = (from: number | null, to: number | null): string => {
    if (from === null && to === null) {
        return ' · click a year to filter';
    }
    if (from !== null && from === to) {
        return ` · ${from} only (click it again to clear)`;
    }
    if (from !== null && to !== null) {
        return ` · ${from} to ${to}`;
    }
    return from !== null ? ` · ${from} onwards` : ` · up to ${to}`;
};

const RecommendationOverview: React.FC<RecommendationOverviewProps> = ({
    shownCount,
    totalCount,
    investigationCount,
    statusCounts,
    byYear,
    selectedStatuses,
    issuedFromYear,
    issuedToYear,
    onToggleStatus,
    onToggleYear,
}) => {
    const statusFaded = (group: SafetyRecommendationStatusGroup): boolean =>
        selectedStatuses.length > 0 && !selectedStatuses.includes(group);
    const statusTotal = STATUS_GROUP_ORDER.reduce((sum, group) => sum + statusCounts[group], 0);
    const donutGroups = STATUS_GROUP_ORDER.filter((group) => statusCounts[group] > 0);

    const donutData: ChartData<'doughnut'> = {
        labels: donutGroups.map((group) => STATUS_GROUP_LABELS[group]),
        datasets: [{
            data: donutGroups.map((group) => statusCounts[group]),
            backgroundColor: donutGroups.map((group) => fill(group, statusFaded(group))),
            hoverBackgroundColor: donutGroups.map((group) => STATUS_STYLE[group].color),
            borderColor: SURFACE,
            borderWidth: 2,
        }],
    };

    const donutOptions: ChartOptions<'doughnut'> = {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '64%',
        animation: {duration: 200},
        onClick: (_event: ChartEvent, elements: ActiveElement[]): void => {
            if (elements.length > 0) onToggleStatus(donutGroups[elements[0].index]);
        },
        onHover: pointerOnMarks,
        plugins: {
            legend: {display: false},
            tooltip: {
                callbacks: {
                    label: (item: TooltipItem<'doughnut'>): string => {
                        const count = item.raw as number;
                        const share = statusTotal > 0 ? Math.round((count / statusTotal) * 100) : 0;
                        return ` ${count} (${share}%)`;
                    },
                },
            },
        },
    };

    // A year column fades when a single year is filtered to and it is not that year.
    const timelineGroups = STATUS_GROUP_ORDER.filter((group) => byYear.counts[group].some((count) => count > 0));
    const timelineData: ChartData<'bar'> = {
        labels: byYear.years.map(String),
        datasets: timelineGroups.map((group) => ({
            label: STATUS_GROUP_LABELS[group],
            data: byYear.counts[group],
            backgroundColor: byYear.years.map(() => fill(group, statusFaded(group))),
            hoverBackgroundColor: STATUS_STYLE[group].color,
            borderColor: SURFACE,
            borderWidth: {top: 2, right: 0, bottom: 0, left: 0},
            borderRadius: 2,
            maxBarThickness: 28,
        })),
    };

    const timelineOptions: ChartOptions<'bar'> = {
        responsive: true,
        maintainAspectRatio: false,
        animation: {duration: 200},
        interaction: {mode: 'index', intersect: false},
        onClick: (_event: ChartEvent, elements: ActiveElement[]): void => {
            if (elements.length > 0) onToggleYear(byYear.years[elements[0].index]);
        },
        onHover: pointerOnMarks,
        scales: {
            x: {
                stacked: true,
                grid: {display: false},
                ticks: {autoSkip: true, maxRotation: 0, font: {size: 11}},
            },
            y: {
                stacked: true,
                beginAtZero: true,
                grid: {color: 'rgba(0, 0, 0, 0.06)'},
                border: {display: false},
                ticks: {precision: 0, font: {size: 11}},
            },
        },
        plugins: {
            legend: {display: false},
            tooltip: {
                filter: (item: TooltipItem<'bar'>): boolean => (item.raw as number) > 0,
                callbacks: {
                    title: (items: TooltipItem<'bar'>[]): string => `Issued ${items[0]?.label ?? ''}`,
                    label: (item: TooltipItem<'bar'>): string => ` ${item.raw as number}  ${item.dataset.label ?? ''}`,
                },
            },
        },
    };

    return (
        <Box sx={{
            display: 'flex', flexWrap: 'wrap', alignItems: 'stretch', gap: 3, px: 2, py: 1.5,
            border: 1, borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper',
        }}>
            {/* FAA status: donut with the total at its centre, and a legend that filters */}
            <Stack direction="row" spacing={2} alignItems="flex-start">
                <Box sx={{flexShrink: 0}}>
                    <Typography variant="overline" color="text.secondary" component="div" sx={{lineHeight: 1.5, textAlign: 'center'}}>
                        {shownCount === totalCount ? 'Recommendations' : `Shown of ${totalCount}`}
                    </Typography>
                    <Box sx={{position: 'relative', width: 148, height: 148}}>
                        {statusTotal > 0 && <Doughnut data={donutData} options={donutOptions} aria-label="FAA status" />}
                        <Box sx={{
                            position: 'absolute', inset: 0, display: 'flex',
                            alignItems: 'center', justifyContent: 'center', pointerEvents: 'none',
                        }}>
                            <Typography sx={{fontSize: '1.75rem', fontWeight: 700, lineHeight: 1}}>{shownCount}</Typography>
                        </Box>
                    </Box>
                    {investigationCount !== undefined && investigationCount > 0 && (
                        <Typography variant="caption" color="text.secondary" component="div"
                            sx={{textAlign: 'center', mt: 0.5}}>
                            {`from ${investigationCount} investigation${investigationCount === 1 ? '' : 's'}`}
                        </Typography>
                    )}
                </Box>
                <Box>
                    <Typography variant="overline" color="text.secondary" sx={{lineHeight: 1.5}}>FAA status</Typography>
                    <Stack spacing={0.25}>
                        {STATUS_GROUP_ORDER.map((group) => {
                            const {color, Icon} = STATUS_STYLE[group];
                            const on = selectedStatuses.includes(group);
                            return (
                                <ButtonBase
                                    key={group}
                                    aria-pressed={on}
                                    aria-label={`${STATUS_GROUP_LABELS[group]}: ${statusCounts[group]}`}
                                    onClick={(): void => onToggleStatus(group)}
                                    sx={{
                                        justifyContent: 'flex-start', gap: 1, px: 1, py: 0.25, borderRadius: 1,
                                        border: `1px solid ${on ? color : 'transparent'}`,
                                        backgroundColor: on ? alpha(color, 0.12) : 'transparent',
                                        opacity: statusFaded(group) ? 0.5 : 1,
                                        '&:hover': {backgroundColor: alpha(color, 0.12)},
                                    }}
                                >
                                    <Icon sx={{fontSize: 18, color}} />
                                    <Typography component="span" variant="body2" sx={{minWidth: 150, textAlign: 'left'}}>
                                        {STATUS_GROUP_LABELS[group]}
                                    </Typography>
                                    <Typography component="span" variant="body2" fontWeight={700}
                                        sx={{minWidth: 28, textAlign: 'right'}}>
                                        {statusCounts[group]}
                                    </Typography>
                                </ButtonBase>
                            );
                        })}
                    </Stack>
                </Box>
            </Stack>

            {/* When they were issued: columns by year, stacked by the same statuses */}
            <Box sx={{flex: '1 1 380px', minWidth: 0, display: 'flex', flexDirection: 'column'}}>
                <Typography variant="overline" color="text.secondary" sx={{lineHeight: 1.5}}>
                    Issued by year{yearCaption(issuedFromYear, issuedToYear)}
                </Typography>
                <Box sx={{position: 'relative', flex: 1, minHeight: 150}}>
                    {byYear.years.length > 0 && <Bar data={timelineData} options={timelineOptions} aria-label="Recommendations issued by year" />}
                </Box>
            </Box>
        </Box>
    );
};

export default RecommendationOverview;
